const TINY_IMAGE_AREA_THRESHOLD = 40 * 40;
const EPHEMERAL_PROTOCOL_PREFIXES = ["blob:", "data:", "file:"] as const;

type SvgDataUrl = `data:image/svg+xml,${string}`;

const makeBlurDataUrl = ({
  base,
  lowlight,
  highlight,
}: {
  base: string;
  lowlight: string;
  highlight: string;
}): SvgDataUrl =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 700 475" preserveAspectRatio="none"><defs><linearGradient id="g"><stop stop-color="${lowlight}" offset="20%"/><stop stop-color="${highlight}" offset="50%"/><stop stop-color="${lowlight}" offset="70%"/></linearGradient></defs><rect width="700" height="475" fill="${base}"/><rect id="r" width="700" height="475" fill="url(#g)"><animate attributeName="x" from="-700" to="700" dur="1.25s" repeatCount="indefinite"/></rect></svg>`,
  )}` as SvgDataUrl;

export const SHARED_LIGHT_BLUR_DATA_URL = makeBlurDataUrl({
  base: "#f3f4f6",
  lowlight: "#e5e7eb",
  highlight: "#ffffff",
});

export const SHARED_DARK_BLUR_DATA_URL = makeBlurDataUrl({
  base: "#101014",
  lowlight: "#18181d",
  highlight: "#2a2a31",
});

export const SHARED_BLUR_DATA_URL = SHARED_DARK_BLUR_DATA_URL;

const toFiniteDimension = (
  value: number | string | undefined,
): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const isEphemeralImageSrc = (src: unknown): boolean => {
  if (typeof src !== "string") {
    return false;
  }

  const normalized = src.trim().toLowerCase();
  if (normalized.length === 0) {
    return true;
  }

  return EPHEMERAL_PROTOCOL_PREFIXES.some((prefix) =>
    normalized.startsWith(prefix),
  );
};

const isTinyExplicitImage = (
  width: number | string | undefined,
  height: number | string | undefined,
): boolean => {
  const widthValue = toFiniteDimension(width);
  const heightValue = toFiniteDimension(height);

  if (!widthValue || !heightValue) {
    return false;
  }

  return widthValue * heightValue < TINY_IMAGE_AREA_THRESHOLD;
};

export type ShouldApplyDefaultBlurOptions = {
  src: unknown;
  width?: number | string;
  height?: number | string;
  disableBlur?: boolean;
  forceBlur?: boolean;
};

export const shouldApplyDefaultBlur = ({
  src,
  width,
  height,
  disableBlur = false,
  forceBlur = false,
}: ShouldApplyDefaultBlurOptions): boolean => {
  if (forceBlur) {
    return true;
  }

  if (disableBlur) {
    return false;
  }

  if (isEphemeralImageSrc(src)) {
    return false;
  }

  if (isTinyExplicitImage(width, height)) {
    return false;
  }

  return true;
};
